import React from 'react';
import { Home, History, LogOut, CreditCard, PenLine } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from '../common/Link';
import { useLocation } from '../../hooks/useLocation';
import { signOutUser } from '../../utils/firebase';

interface SideMenuProps {
  onClose?: () => void;
}

const menuItems = [
  { icon: Home, label: 'Home', href: '/' },
  { icon: History, label: 'History', href: '/dashboard/history' },
  { icon: PenLine, label: 'New Job Kit', href: '/generator', isPrimary: true },
];

export function SideMenu({ onClose }: SideMenuProps) {
  const { user, subscription } = useAuth();
  const { pathname } = useLocation();

  const handleNavigation = () => {
    if (onClose) {
      onClose();
    }
  };

  if (!user) return null;

  const getSubscriptionTitle = () => {
    if (subscription) {
      return (
        <>
          <div className="text-primary font-medium text-base border-t border-gray-100 pt-2 mt-2">
            Pro Plan
          </div>
          <a
            href="https://billing.stripe.com/p/login/7sI9Eo0Ad7Jm8pi000"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center space-x-1 text-xs text-slate-light hover:text-primary transition-colors mt-1"
          >
            <CreditCard className="w-3 h-3" />
            <span>Manage Subscription</span>
          </a>
        </>
      );
    }

    return (
      <div className="text-slate-light font-medium text-base border-t border-gray-100 pt-2 mt-2">
        Free Plan
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col bg-white">
      <div className="p-4 md:p-6 border-b">
        <div className="flex flex-col items-center">
          <img
            src={user.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.email || '')}`}
            alt={user.displayName || 'User'}
            className="w-12 h-12 md:w-16 md:h-16 rounded-full mb-2 md:mb-3"
          />
          <div className="text-center">
            <div className="font-medium text-slate text-sm md:text-base">
              {user.displayName || user.email?.split('@')[0]}
            </div>
            <div className="text-xs md:text-sm text-slate-light truncate max-w-[180px]">
              {user.email}
            </div>
            {getSubscriptionTitle()}
          </div>
        </div>

        {!subscription && (
          <div className="mt-4">
            <Link
              href="/pricing"
              onClick={handleNavigation}
              className="block w-full text-center text-white bg-success hover:bg-success-hover focus:ring-4 focus:ring-success/30 font-medium rounded-lg text-xs px-4 py-2 md:px-5 md:py-2.5 focus:outline-none transition-colors"
            >
              Upgrade to Premium
            </Link>
          </div>
        )}
      </div>

      <nav className="flex-1 p-3 md:p-4">
        {menuItems.map((item) => {
          const Icon = item.icon;
          const isActive = pathname === item.href;
          return (
            <Link
              key={item.href}
              href={item.href}
              onClick={handleNavigation}
              className={`flex items-center space-x-3 px-3 py-2.5 md:px-4 md:py-3 rounded-lg transition-colors mb-2 ${
                item.isPrimary 
                  ? 'bg-primary text-white hover:bg-primary-hover' 
                  : isActive 
                    ? 'bg-primary/10 text-primary' 
                    : 'text-slate hover:bg-slate/5'
              }`}
            >
              <Icon className="w-4 h-4 md:w-5 md:h-5" />
              <span className="text-sm md:text-base">{item.label}</span>
            </Link>
          );
        })}
      </nav>

      <div className="p-3 md:p-4 border-t">
        <button
          onClick={() => {
            signOutUser();
            handleNavigation();
          }}
          className="flex items-center space-x-3 px-3 py-2.5 md:px-4 md:py-3 w-full text-slate hover:bg-slate/5 rounded-lg transition-colors"
        >
          <LogOut className="w-4 h-4 md:w-5 md:h-5" />
          <span className="text-sm md:text-base">Sign Out</span>
        </button>
      </div>
    </div>
  );
}